import React, { useEffect, useState } from "react";
import { ImageInput, ImageField, FormDataConsumer } from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import { useForm } from "react-final-form";

const useStyles = makeStyles({
    inputRoot: {
        width: "320px",
        maxWidth: "100%",
    },
    dropZoneFullWidth: {
        width: "100% !important",
    },
    hiddenPreview: {
        display: "none",
    },
    inlineRow: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "nowrap",
    },
    previewColumn: {
        flex: "0 0 auto",
        marginLeft: "16px"
    },
    previewWrap: {
        marginTop: "0.25rem",
        marginBottom: "1.75rem",
        position: "relative",
        display: "inline-block",
    },
    previewImg: {
        width: "135px",
        height: "auto",
        display: "block",
        maxWidth: "100%",
        objectFit: "contain",
    },
    removeButton: {
        position: "absolute",
        top: "-10px",
        right: "-10px",
        backgroundColor: "#fff5f5",
        border: "1px solid #fca5a5",
        color: "#dc2626",
        "&:hover": {
            backgroundColor: "#fee2e2",
        },
    },
});

function appendCacheBuster(url) {
    if (!url || typeof url !== "string" || url.startsWith("blob:")) {
        return url;
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}random=${Math.random()}`;
}

function resolveStoreLogoSrc(logo) {
    if (!logo) return null;
    if (Array.isArray(logo) && logo.length > 0) {
        return resolveStoreLogoSrc(logo[0]);
    }
    if (typeof logo === "string") return appendCacheBuster(logo);
    if (logo.src) return appendCacheBuster(logo.src);
    if (logo instanceof File) return URL.createObjectURL(logo);
    if (logo.rawFile instanceof File) return URL.createObjectURL(logo.rawFile);
    return null;
}

function StoreLogoPreview({ logo, onRemove }) {
    const classes = useStyles();
    const [url, setUrl] = useState(null);

    useEffect(() => {
        const next = resolveStoreLogoSrc(logo);
        setUrl(next);
        return () => {
            if (next && typeof next === "string" && next.startsWith("blob:")) {
                URL.revokeObjectURL(next);
            }
        };
    }, [logo]);

    if (!url) return null;

    return (
        <div className={classes.previewWrap}>
            <IconButton
                size="small"
                className={classes.removeButton}
                onClick={onRemove}
                aria-label="Remove store logo"
                title="Remove logo"
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
            <img src={url} alt="Store logo preview" className={classes.previewImg} />
        </div>
    );
}

export default function StoreLogoInput({ initialLogo = null }) {
    const classes = useStyles();
    const form = useForm();

    const handleRemoveLogo = () => {
        form.change("store_logo", null);
    };

    return (
        <div className={classes.inlineRow}>
            <div>
                <ImageInput
                    source="store_logo"
                    accept="image/*"
                    label="Store logo"
                    className={classes.inputRoot}
                    classes={{
                        dropZone: classes.dropZoneFullWidth,
                        preview: classes.hiddenPreview,
                        removeButton: classes.hiddenPreview,
                    }}
                >
                    <ImageField source="src" title="Store logo" />
                </ImageInput>
            </div>
            <div className={classes.previewColumn}>
                <FormDataConsumer>
                    {({ formData }) => {
                        const hasStoreLogoValue =
                            !!formData &&
                            Object.prototype.hasOwnProperty.call(formData, "store_logo");
                        const logo = hasStoreLogoValue
                            ? formData.store_logo
                            : initialLogo;
                        return <StoreLogoPreview logo={logo} onRemove={handleRemoveLogo} />;
                    }}
                </FormDataConsumer>
            </div>
        </div>
    );
}
